import React, { useState, useEffect } from "react";
import { adminCategoryService, CreateCategoryRequest } from "../../services/admin/categoryService";
import { Category } from "../../services/categoryService";
import CategoryForm from "../../components/admin/CategoryForm";
import ConfirmModal from "../../components/admin/ConfirmModal";

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminCategoryService.getAll();
      setCategories(data);
    } catch (err: any) {
      setError(err.message || "فشل تحميل الفئات");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete({ id: category.id, name: category.name });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      await adminCategoryService.delete(categoryToDelete.id);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      await loadCategories();
    } catch (err: any) {
      alert(err.message || "فشل حذف الفئة");
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleFormSubmit = async (data: CreateCategoryRequest) => {
    try {
      if (editingCategory) {
        await adminCategoryService.update(editingCategory.id, data);
      } else {
        await adminCategoryService.create(data);
      }
      setShowForm(false);
      setEditingCategory(null);
      await loadCategories();
    } catch (err: any) {
      alert(err.message || "فشل حفظ الفئة");
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  if (loading) {
    return <div className="text-center py-8">جاري التحميل...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
          إدارة الفئات
        </h1>
        <button
          onClick={handleCreate}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
        >
          إضافة فئة جديدة
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {showForm && (
        <CategoryForm
          category={editingCategory}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-700">
            <tr>
              <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                الاسم
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                الوصف
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                ترتيب العرض
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                الحالة
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                الإجراءات
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {categories.map((category) => (
              <tr key={category.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white">
                  {category.name}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                  {category.description || "-"}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                  {category.displayOrder}
                </td>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`px-2 py-1 rounded ${
                      category.isActive
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                    }`}
                  >
                    {category.isActive ? "نشط" : "غير نشط"}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                    >
                      تعديل
                    </button>
                    <button
                      onClick={() => handleDeleteClick(category)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {categories.length === 0 && (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            لا توجد فئات
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="تأكيد حذف الفئة"
        message={`هل أنت متأكد من حذف الفئة "${categoryToDelete?.name}"؟ لا يمكن التراجع عن هذا الإجراء.`}
        confirmText="نعم، حذف الفئة"
        cancelText="إلغاء"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />
    </div>
  );
};

export default Categories;

